import React, { useEffect, useMemo, useState } from 'react';
import cls from 'classnames';
import { Left, LeftSmall, Right } from '@icon-park/react';
import ImagePreview from './previews/image-preview';
import { FilePreviewFileInfoType, FilePreviewProps } from './types';
import VideoPreview from './previews/video-preview';
import CommonPreview from './previews/common-preview';
import './index.scss';

const classPrefix = 'asset-management';

function FilePreview(props: FilePreviewProps) {
  const { className, show, defaultIndex, fileList, onClose, onClickAction } = props;
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (defaultIndex !== undefined) {
      setCurrentIndex(defaultIndex);
    }
  }, [defaultIndex]);

  const currentFile = useMemo(() => fileList[currentIndex], [currentIndex, fileList]);

  const showLeftIcon = useMemo(() => fileList.length > 1 && currentIndex !== 0, [currentIndex, fileList]);

  const showRightIcon = useMemo(
    () => fileList.length > 1 && currentIndex !== fileList.length - 1,
    [currentIndex, fileList]
  );

  function onNext() {
    setCurrentIndex(Math.min(currentIndex + 1, fileList.length - 1));
  }

  function onPre() {
    setCurrentIndex(Math.max(currentIndex - 1, 0));
  }

  return show ? (
    <div className={cls(`${classPrefix}-file-preview`, `file-preview-type-${currentFile?.type}`, className)}>
      {showLeftIcon && (
        <div className={`${classPrefix}-file-preview-action left`} onClick={onPre}>
          <Left size={20} />
        </div>
      )}
      <div className={`${classPrefix}-file-preview-header`}>
        <div className={`${classPrefix}-file-preview-header-left`}>
          <div className={`${classPrefix}-file-preview-header-back`} onClick={onClose}>
            <LeftSmall size={32} />
          </div>
          <div className={`${classPrefix}-file-preview-header-file-meta`}>
            <div className={cls(`${classPrefix}-file-preview-header-name`)}>
              <span>{currentFile?.name}</span>
            </div>
            <div className={cls(`${classPrefix}-file-preview-header-file-data`)}>
              {(currentFile?.data || []).map((item, index) => (
                <span key={index} className={cls(`${classPrefix}-file-preview-header-file-data-item`)}>
                  {item.icon && <span style={{ paddingRight: '8px' }}>{item.icon}</span>}
                  <span>{item.value}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className={`${classPrefix}-file-preview-header-file-actions`}>
          {(currentFile?.actions || []).map((item) => (
            <span
              className={`${classPrefix}-file-preview-header-file-actions-item`}
              key={item.type}
              onClick={() => onClickAction?.(currentFile, item.type)}
            >
              {item.icon}
            </span>
          ))}
        </div>
      </div>
      <div className={cls(`${classPrefix}-file-preview-content`, `file-type-${currentFile?.type}`)}>
        {currentFile?.type === FilePreviewFileInfoType.IMAGE && (
          <ImagePreview value={currentFile} key={currentFile.id} />
        )}
        {currentFile?.type === FilePreviewFileInfoType.VIDEO && (
          <VideoPreview value={currentFile} key={currentFile.id} />
        )}
        {currentFile?.type === FilePreviewFileInfoType.OTHER && (
          <CommonPreview value={currentFile} key={currentFile.id} />
        )}
      </div>
      {showRightIcon && (
        <div className={`${classPrefix}-file-preview-action right`} onClick={onNext}>
          <Right size={20} />
        </div>
      )}
      <div className={`${classPrefix}-file-preview-pagination`}>
        {`${currentIndex + 1} / ${fileList.length}`}
      </div>
    </div>
  ) : null;
}

export default FilePreview;
